import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../hooks/useAuth"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "../components/Button"
import { useToast } from "@/hooks/use-toast"

// 🧠 Schema for login validation
const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
})

// 🧠 Schema for signup validation (adds role)
const signupSchema = loginSchema.extend({
  role: z.enum(["driver", "dispatcher"], {
    invalid_type_error: "Please select a role",
  }),
})

// ✨ Type-safe form values
type LoginFormData = z.infer<typeof loginSchema>
type SignupFormData = z.infer<typeof signupSchema>

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true)
  const { user, role, isLoading, signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  // 🎯 Login form setup
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  })

  // 🎯 Signup form setup
  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: "", password: "", role: "driver" },
  })

  // 🔁 Auto-redirect if already logged in
  useEffect(() => {
    if (user && role) navigate(`/${role}/dashboard`, { replace: true })
  }, [user, role, navigate])

  // 🔐 Handle login
  const handleLogin = async (data: LoginFormData) => {
    const { error } = await signIn(data.email, data.password)
    toast({
      title: error ? "Login Failed" : "Success",
      description: error ? error.message : "You are now signed in",
      variant: error ? "error" : "success",
    })
  }

  // ✍️ Handle signup
  const handleSignup = async (data: SignupFormData) => {
    const { error } = await signUp(data.email, data.password, data.role)
    toast({
      title: error ? "Signup Failed" : "Account Created",
      description: error
        ? error.message
        : "Check your email to verify your account",
      variant: error ? "error" : "success",
    })
  }

  // 💫 Show spinner while loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* 💡 Auth Box */}
      <div className="flex flex-col justify-center flex-1 px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="w-full max-w-sm mx-auto lg:w-96">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              {isLogin ? "Sign in to your account" : "Create a new account"}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="font-medium text-primary hover:text-primary/90"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>

          {/* 👤 Login Form */}
          {isLogin ? (
            <form
              onSubmit={loginForm.handleSubmit(handleLogin)}
              className="space-y-6"
            >
              <div>
                <label htmlFor="login-email" className="label">Email address</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  {...loginForm.register("email")}
                  className="input"
                />
                {loginForm.formState.errors.email && (
                  <p className="error">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="login-password" className="label">Password</label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  {...loginForm.register("password")}
                  className="input"
                />
                {loginForm.formState.errors.password && (
                  <p className="error">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <Button type="submit" variant="primary" className="w-full">
                {loginForm.formState.isSubmitting ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          ) : (
            // 🆕 Signup Form
            <form
              onSubmit={signupForm.handleSubmit(handleSignup)}
              className="space-y-6"
            >
              <div>
                <label htmlFor="signup-email" className="label">Email address</label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  {...signupForm.register("email")}
                  className="input"
                />
                {signupForm.formState.errors.email && (
                  <p className="error">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="label">Password</label>
                <input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  {...signupForm.register("password")}
                  className="input"
                />
                {signupForm.formState.errors.password && (
                  <p className="error">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="role" className="label">Role</label>
                <select
                  id="role"
                  {...signupForm.register("role")}
                  className="input"
                >
                  <option value="driver">Driver</option>
                  <option value="dispatcher">Dispatcher</option>
                </select>
                {signupForm.formState.errors.role && (
                  <p className="error">{signupForm.formState.errors.role.message}</p>
                )}
              </div>

              <Button type="submit" variant="primary" className="w-full">
                {signupForm.formState.isSubmitting
                  ? "Creating account..."
                  : "Create account"}
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* 🌈 Right Side: Gradient Brand */}
      <div className="relative flex-1 hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark">
          <div className="flex items-center justify-center h-full p-12">
            <div className="max-w-xl text-white">
              <h2 className="text-4xl font-bold">Logistics Buddy</h2>
              <p className="mt-4 text-xl">
                Connecting drivers and dispatchers for efficient logistics management.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
